import { adminPanelHref } from "@/lib/admin-panel-url";
import { useState } from "react";
import { NewPortalSwitch } from "./customer-portal-switch";
import { CustomerPortalTestOrder } from "./customer-portal-test-order";
import type { PortalShop } from "./customer-portal-types";

function BoolBadge({ value, label }: { value: boolean; label: string }) {
  return (
    <span
      title={label}
      className={[
        "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
        value
          ? "bg-emerald-50 text-emerald-600"
          : "bg-gray-100 text-muted-foreground",
      ].join(" ")}
    >
      {value ? "✓" : "–"}
    </span>
  );
}

/** Last return request, in months when it is old enough for that to be what matters. */
function LastActivity({ ts }: { ts: number | null }) {
  // Read once: render must stay pure.
  const [now] = useState(() => Date.now());

  if (!ts) {
    return (
      <span
        title="Aucun retour enregistré"
        className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500"
      >
        aucun retour
      </span>
    );
  }

  const days = Math.round((now - ts) / 86_400_000);
  const label = days >= 60 ? `${Math.round(days / 30)} mois` : `${days} j`;

  return (
    <span
      title={`Dernier retour : ${new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(ts))}`}
      className={[
        "whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium",
        days >= 180
          ? "bg-red-50 text-red-600"
          : days >= 90
            ? "bg-amber-50 text-amber-700"
            : "bg-emerald-50 text-emerald-700",
      ].join(" ")}
    >
      {label}
    </span>
  );
}

export function CustomerPortalRow({ shop }: { shop: PortalShop }) {
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <a
            href={adminPanelHref(`/shared/shops/${shop.id}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:text-violet-600 hover:underline"
          >
            {shop.name}
          </a>
          <LastActivity ts={shop.lastActivityAt ?? null} />
        </div>
        <div className="text-xs text-muted-foreground">{shop.slug}</div>
      </td>
      <td className="px-3 py-2">
        <CustomerPortalTestOrder
          shopId={shop.id}
          shopifyUrl={shop.shopifyUrl}
          providerId={shop.providerId}
          storefrontUrl={shop.storefrontUrl ?? null}
        />
      </td>
      <td className="px-3 py-2">
        <NewPortalSwitch
          shopId={shop.id}
          isEnabled={shop.isNewCustomerPortalEnabled ?? false}
          legacyPortalReason={shop.legacyPortalReason ?? null}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasExchanges ?? false} label="Échanges" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge
          value={shop.hasExchangeCredit ?? false}
          label="Échanges crédit"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasGiftCode ?? false} label="Avoirs" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge
          value={shop.hasCustomColors ?? false}
          label="Couleurs perso"
        />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.hasCustomCss ?? false} label="CSS perso" />
      </td>
      <td className="px-3 py-2 text-center">
        <BoolBadge value={shop.isActive ?? true} label="Actif" />
      </td>
    </tr>
  );
}
